import React, { useEffect, useState, useCallback } from 'react';
import { Form } from 'react-bootstrap';



import EnergyTracerMultiLineChart, { createDownloadHeadersAndKeys, SELECTION_KEY, SELECTION_BASELINE_KEY, SELECTION_BASELINE2_KEY, ToggleBaseline } from '../../components/EnergyTracerMultiLineChart';
import CheckResources from './CheckResources';
import {
  BarChart,
  ResponsiveContainer,
  YAxis,
  XAxis,
  Tooltip,
  Bar,
  Legend,
} from 'recharts';
import CHARTCOLORS from '../../helpers/chartColors';
import formatValue from '../../helpers/chartFormatter';
import consumptionUnits, { UNIT_UTILITIES, ET_FRONTEND_CONVERSIONS } from '../../helpers/consumptionUnits';
import DashboardBaselineDateSelectContainer from './DashboardBaselineDateSelectContainer';
import {
  GHG_UTILITIES,
  ghg_units,
  short_ghg_units,
  ghg_conversions,
} from '../../helpers/GHGUnits';
import GraphActionContainer from '../../components/chartElements/GraphActionContainer';
import ChartDownload from '../../components/chartElements/Actions/ChartDownload';
import ChartToggle from '../../components/chartElements/Actions/ChartToggle';
import UtilityBreakdown from './UtilityBreakdown'
import OtherUtilityTrends from './OtherUtilityTrends';

import WaterUtilityTrends from './WaterUtilityTrends';

const row = {
  marginRight: '40px',
  paddingLeft: '1em',
  marginTop: '1em',
};

const styles = {
  inputStyle: {
    width: '200px',
    textAlign: 'center',
    marginRight: '10px',
  },
  unit_form: { display: 'inline-block', marginLeft: '1em' },
};

export default function UtilityTrends(props) {
  const [deferredProps, setDeferredProps] = useState({ ...props });
  useEffect(() => {
    if (props.open) setDeferredProps({ ...props });
  }, [props]);
  const {
    dashboardData,
    organization,
    endDate,
    dateOption,
    activeIds,
    activeKeys,
    activeGHGLicense,
    open,
  } = deferredProps;

  const [utilitySelected, setUtilitySelected] = useState('none');
  const [activeUtilities, setActiveUtilities] = useState([]);
  const [unitSelected, setUnitSelected] = useState('');

  const [activeGHGUnit, setActiveGHGUnit] = useState('lbs');
  const [GHGUnitFactor, setGHGUnitFactor] = useState(1);

  const [costData, setCostData] = useState([]);
  const [usageData, setUsageData] = useState([]);
  const [demandData, setDemandData] = useState([]);
  const [GHGData, setGHGData] = useState([]);
  const [convertedGHGData, setConvertedGHGData] = useState([]);
  const [costBarData, setCostBarData] = useState([]);
  const [costBarKeys, setCostBarKeys] = useState([]);

  const [costSummary, setCostSummary] = useState(true);
  const [usageSummary, setUsageSummary] = useState(true);
  const [demandSummary, setDemandSummary] = useState(true);
  const [GHGSummary, setGHGSummary] = useState(true);

  const [chartDownloadHeaders, setChartDownloadHeaders] = useState([]);
  const [chartDownloadKeys, setChartDownloadKeys] = useState([]);
  const [chartCostBarDownloadHeaders, setChartCostBarDownloadHeaders] = useState([]);
  const [chartCostBarDownloadKeys, setChartCostBarDownloadKeys] = useState([])

  const [baselineData, setBaselineData] = useState({});
  const [baseline2Data, setBaseline2Data] = useState({});

  const [showBaselineData, setShowBaselineData] = useState(true)
  const [showBaseline2Data, setShowBaseline2Data] = useState(true)


  useEffect(() => {
    setGHGUnitFactor(1 / ghg_conversions[activeGHGUnit]);
  }, [activeGHGUnit]);

  useEffect(() => {
    let altered_ghg_data = [];
    GHGData.forEach((month) => {
      let temp_month = {};
      Object.keys(month).forEach((key) => {
        if (['name', 'month'].includes(key)) {
          temp_month[key] = month[key];
        } else {
          temp_month[key] = month[key] * GHGUnitFactor;
        }
      });
      altered_ghg_data.push(temp_month);
    });
    setConvertedGHGData(altered_ghg_data);
  }, [GHGData, GHGUnitFactor]);

  const convertToChartForm = useCallback(
    (
      dashboardData,
      baselineData,
      baseline2Data,
      utilitySelected,
      unitSelected,
      chartType,
      activeIds,
      activeKeys
    ) => {
      const hasBaseline = Object.keys(baselineData).length > 0;
      const hasBaseline2 = Object.keys(baseline2Data).length > 0;
      let data = dashboardData['month_array'].map((month) => {
        return {
          name: month,
          month: month,
          [SELECTION_KEY]: 0,
          [SELECTION_BASELINE_KEY]: 0,
          [SELECTION_BASELINE2_KEY]: 0,
        };
      });
      let factor = 1;
      if (
        UNIT_UTILITIES.includes(utilitySelected) &&
        chartType === 'usage' &&
        Object.keys(ET_FRONTEND_CONVERSIONS).includes(utilitySelected) &&
        Object.keys(ET_FRONTEND_CONVERSIONS[utilitySelected]).includes(
          unitSelected
        )
      ) {
        factor = ET_FRONTEND_CONVERSIONS[utilitySelected][unitSelected];
      }

      data.forEach((entry, index) => {
        activeIds.forEach((id, index2) => {
          let key = activeKeys[index2];

          let val = dashboardData['building_usage'][id] &&
            dashboardData['building_usage'][id][utilitySelected]
            ? dashboardData['building_usage'][id][utilitySelected][chartType][
            index
            ] * factor
            : 0;
          entry[key] = val
          entry[SELECTION_KEY] += val
          if (hasBaseline) {
            let baseline_val = baselineData['building_usage'][id] &&
              baselineData['building_usage'][id][utilitySelected]
              ? baselineData['building_usage'][id][utilitySelected][
              chartType
              ][index] * factor
              : 0
            entry[key + ' Baseline'] = baseline_val;
            entry[SELECTION_BASELINE_KEY] += baseline_val
          }
          if (hasBaseline2) {
            let baseline2_val = baseline2Data['building_usage'][id] &&
              baseline2Data['building_usage'][id][utilitySelected]
              ? baseline2Data['building_usage'][id][utilitySelected][
              chartType
              ][index] * factor
              : 0;
            entry[key + ' Baseline 2'] = baseline2_val
            entry[SELECTION_BASELINE2_KEY] += baseline2_val
          }
        });
      });
      return data;
    },
    []
  );

  const convertBarCosts = useCallback(
    (dashboardData, utilitySelected, activeIds) => {
      let cost_bar_keys = [];
      let local_ids = [...activeIds];

      let data = dashboardData['month_array'].map((month) => {
        return {
          name: month,
          month: month,
        };
      });
      data.forEach((entry, index) => {
        local_ids.forEach((id) => {
          let costs = {};
          if (
            dashboardData['building_usage'][id] &&
            dashboardData['building_usage'][id][utilitySelected] &&
            dashboardData['building_usage'][id][utilitySelected]['costs']
          ) {
            costs =
              dashboardData['building_usage'][id][utilitySelected]['costs'];
          }
          Object.keys(costs).forEach((cost) => {
            if (!cost_bar_keys.includes(cost) && costs[cost][index] !== 0) {
              cost_bar_keys.push(cost);
            }
            if (entry[cost]) {
              entry[cost] += costs[cost][index];
            } else {
              entry[cost] = costs[cost][index];
            }
          });
        });
      });
      setCostBarKeys(cost_bar_keys);
      return data;
    },
    []
  );




  const setOrgData = useCallback(() => {
    if (Object.keys(dashboardData).length) {
      const utilities = [...dashboardData['available_utilities'], 'total']
      setActiveUtilities(utilities);
      const utility = utilities.includes(
        utilitySelected
      )
        ? utilitySelected
        : dashboardData['available_utilities'][0];

      setUtilitySelected(utility);
      setCostData(
        convertToChartForm(
          dashboardData,
          baselineData,
          baseline2Data,
          utility,
          unitSelected,
          'cost',
          activeIds,
          activeKeys
        )
      );
      setUsageData(
        convertToChartForm(
          dashboardData,
          baselineData,
          baseline2Data,
          utility,
          unitSelected,
          'usage',
          activeIds,
          activeKeys
        )
      );
      setDemandData(
        convertToChartForm(
          dashboardData,
          baselineData,
          baseline2Data,
          utility,
          unitSelected,
          'demand',
          activeIds,
          activeKeys
        )
      );

      setGHGData(
        convertToChartForm(
          dashboardData,
          baselineData,
          baseline2Data,
          utility,
          unitSelected,
          'ghg',
          activeIds,
          activeKeys
        )
      );
      setCostBarData(convertBarCosts(dashboardData, utility, activeIds));

    } else {
      setActiveUtilities([]);
      setCostData([]);
      setUsageData([]);
      setDemandData([]);
      setGHGData([]);
      setConvertedGHGData([]);
    }
  }, [
    dashboardData,
    baselineData,
    baseline2Data,
    convertToChartForm,
    convertBarCosts,
    utilitySelected,
    unitSelected,
    activeIds,
    activeKeys,
  ]);

  useEffect(() => {
    if (UNIT_UTILITIES.includes(utilitySelected)) {
      setUnitSelected(consumptionUnits[utilitySelected][0]);
    } else if (utilitySelected === 'electric') {
      setUnitSelected('kwh');
    } else {
      setUnitSelected('');
    }
  }, [utilitySelected]);

  useEffect(() => {
    setOrgData();
  }, [setOrgData]);

  useEffect(() => {
    const hasBaseline = Object.keys(baselineData).length > 0;
    const hasBaseline2 = Object.keys(baseline2Data).length > 0;

    createDownloadHeadersAndKeys(
      activeKeys, setChartDownloadHeaders, setChartDownloadKeys, hasBaseline, hasBaseline2
    )
  }, [activeKeys, baselineData, baseline2Data]);

  useEffect(() => {
    let headers = ['Date'];
    let keys = ['name'];
    costBarKeys.forEach((key) => {
      headers.push(key);
      keys.push(key);
    });
    setChartCostBarDownloadHeaders(headers);
    setChartCostBarDownloadKeys(keys);
  }, [costBarKeys]);




  useEffect(() => {
    if (Object.keys(baselineData).length === 0) {
      setBaseline2Data({})
    }
  }
    , [baselineData])


  return (
    <div>
      <CheckResources
        organization={organization}
        dashboardData={dashboardData}
      />
      <div>
        <div style={{ display: 'inline-block' }}>
          <Form inline>
            <Form.Group>
              <Form.Label style={{ display: 'block', marginRight: '5px' }}>
                Utility:
              </Form.Label>
              <Form.Control
                style={styles.inputStyle}
                as='select'
                size='sm'
                onChange={(e) => {
                  setUtilitySelected(e.target.value);
                }}
                value={utilitySelected}
                data-id='db_org_utility'
              >
                {activeUtilities.map((utility) => (
                  <option
                    key={`util-tab-${utility.toLowerCase()}`}
                    value={utility.toLowerCase()}
                  >
                    {utility.charAt(0).toUpperCase() +
                      utility.substr(1).toLowerCase()}
                  </option>
                ))}
              </Form.Control>
            </Form.Group>
            {UNIT_UTILITIES.includes(utilitySelected) && (
              <Form.Group>
                <Form.Label style={{ display: 'block', marginRight: '5px' }}>
                  Unit:
                </Form.Label>
                <Form.Control
                  style={styles.inputStyle}
                  as='select'
                  size='sm'
                  onChange={(e) => {
                    setUnitSelected(e.target.value);
                  }}
                  value={unitSelected}
                  data-id='db_org_unit'
                >
                  {consumptionUnits[utilitySelected].map((unit) => (
                    <option key={`unit-tab-${unit}`} value={unit}>
                      {unit}
                    </option>
                  ))}
                </Form.Control>
              </Form.Group>
            )}
          </Form>
        </div>
      </div>
      <div style={{ display: 'inline-block', margin: '1em 0em' }}>
        <DashboardBaselineDateSelectContainer
          orgId={organization.id}
          endDate={endDate}
          dateOption={dateOption}
          setBaselineData={setBaselineData}
        />
      </div>
      {Object.keys(baselineData).length > 0 &&
        <ToggleBaseline
          showBaselineData={showBaselineData}
          setShowBaselineData={setShowBaselineData}
        />
      }
      {Object.keys(baselineData).length > 0 &&
        <div style={{ display: 'inline-block', margin: '1em 0em', marginLeft: '2em' }}>
          <DashboardBaselineDateSelectContainer
            orgId={organization.id}
            endDate={endDate}
            dateOption={dateOption}
            setBaselineData={setBaseline2Data}
            baselineIndex={2}
          />
        </div>
      }
      {Object.keys(baselineData).length > 0 && Object.keys(baseline2Data).length > 0 &&
        <ToggleBaseline
          showBaselineData={showBaseline2Data}
          setShowBaselineData={setShowBaseline2Data}
        />
      }


      {utilitySelected === 'total' &&
        <UtilityBreakdown
          dashboardData={dashboardData}
          baselineData={baselineData}
          baseline2Data={baseline2Data}
          showBaselineData={showBaselineData}
          showBaseline2Data={showBaseline2Data}
          organization={organization}
          open={open}
          activeIds={activeIds}
          activeKeys={activeKeys}
          activeGHGLicense={activeGHGLicense}
        />}
      {utilitySelected === 'water' &&
        <WaterUtilityTrends
          dashboardData={dashboardData}
          baselineData={baselineData}
          baseline2Data={baseline2Data}
          showBaselineData={showBaselineData}
          showBaseline2Data={showBaseline2Data}
          organization={organization}
          open={open}
          activeIds={activeIds}
          activeKeys={activeKeys}
          unitSelected={unitSelected}
        />
      }
      {utilitySelected === 'other' &&
        <OtherUtilityTrends
          dashboardData={dashboardData}
          baselineData={baselineData}
          baseline2Data={baseline2Data}
          showBaselineData={showBaselineData}
          showBaseline2Data={showBaseline2Data}
          organization={organization}
          open={open}
          activeIds={activeIds}
          activeKeys={activeKeys}
        />
      }

      <div style={{ display: !['total', 'other', 'water'].includes(utilitySelected) ? 'block' : 'none' }}>

        <h5>{'Total Cost ($)'} </h5>
        <div style={{ ...row, marginBottom: '4em' }}>
          <EnergyTracerMultiLineChart
            data={!costData ? [] : costData}
            unit={'$'}
            keys={activeKeys}
            baseline={Object.keys(baselineData).length > 0 && showBaselineData}
            baseline2={Object.keys(baseline2Data).length > 0 && showBaseline2Data}
            showSummary={costSummary}
          />
          <GraphActionContainer>
            <ChartToggle
              onClick={() => {
                setCostSummary((prev) => !prev);
              }}
              state={costSummary ? 1 : 0}
              title={'Toggle Selection Total'}
              variant={3}
            />
            <ChartDownload
              organization_id={organization.id}
              data={costData}
              headers={chartDownloadHeaders}
              keys={chartDownloadKeys}
              chart_type={'line'}
            />
          </GraphActionContainer>
        </div>
        {costBarKeys.length > 1 && (
          <>
            <h5>{'Cost Breakdown ($)'}</h5>
            <div style={{ ...row, marginBottom: '4em' }}>
              <ResponsiveContainer width={'100%'} height={300} debounce={300}>
                <BarChart data={costBarData}>
                  <YAxis
                    type='number'
                    tickFormatter={(tick) => {
                      return tick.toLocaleString();
                    }}
                  />
                  <XAxis type='category' dataKey='name' />
                  <Tooltip />
                  <Legend
                    wrapperStyle={{ bottom: '0px', position: 'relative' }}
                  />
                  {costBarKeys.map((cost, idx) => (
                    <Bar
                      dataKey={cost}
                      fill={CHARTCOLORS[idx]}
                      formatter={(value) => formatValue(value, '$')}
                      stackId='a'
                      key={cost + '_' + idx}
                    ></Bar>
                  ))}
                </BarChart>
              </ResponsiveContainer>
              <GraphActionContainer customStyle={{ marginTop: '1em' }}>
                <ChartDownload
                  organization_id={organization.id}
                  data={costBarData}
                  headers={chartCostBarDownloadHeaders}
                  keys={chartCostBarDownloadKeys}
                  chart_type={'bar'}
                />
              </GraphActionContainer>
            </div>
          </>
        )}

        {utilitySelected !== 'other' &&
          <>
            <h5>{'Total Consumption (' + unitSelected + ')'} </h5>
            <div style={{ ...row, marginBottom: '4em' }}>
              <EnergyTracerMultiLineChart
                data={!usageData ? [] : usageData}
                unit={unitSelected}
                keys={activeKeys}
                baseline={Object.keys(baselineData).length > 0 && showBaselineData}
                baseline2={Object.keys(baseline2Data).length > 0 && showBaseline2Data}
                showSummary={usageSummary}
              />
              <GraphActionContainer>
                <ChartToggle
                  onClick={() => {
                    setUsageSummary((prev) => !prev);
                  }}
                  state={usageSummary ? 1 : 0}
                  title={'Toggle Selection Total'}
                  variant={3}
                />
                <ChartDownload
                  organization_id={organization.id}
                  data={usageData}
                  headers={chartDownloadHeaders}
                  keys={chartDownloadKeys}
                  chart_type={'line'}
                />
              </GraphActionContainer>
            </div></>}
        {utilitySelected === 'electric' && (
          <>
            <h5>{'Total Demand (kW)'}</h5>
            <div style={{ ...row, marginBottom: '6em' }}>
              <EnergyTracerMultiLineChart
                data={!demandData ? [] : demandData}
                unit={'kW'}
                keys={activeKeys}
                baseline={Object.keys(baselineData).length > 0 && showBaselineData}
                baseline2={Object.keys(baseline2Data).length > 0 && showBaseline2Data}
                showSummary={demandSummary}
              />
              <GraphActionContainer>
                <ChartToggle
                  onClick={() => {
                    setDemandSummary((prev) => !prev);
                  }}
                  state={demandSummary ? 1 : 0}
                  title={'Toggle Selection Total'}
                  variant={3}
                />
                <ChartDownload
                  organization_id={organization.id}
                  data={demandData}
                  headers={chartDownloadHeaders}
                  keys={chartDownloadKeys}
                  chart_type={'line'}
                />
              </GraphActionContainer>
            </div>
          </>
        )}
        {GHG_UTILITIES.includes(utilitySelected) && activeGHGLicense && (
          <>
            <h5>
              {'Total Emissions'}

              <Form.Group controlId={'ghg-unit-select'} style={styles.unit_form}>
                <Form.Control
                  as='select'
                  onChange={(e) => setActiveGHGUnit(e.target.value)}
                  defaultValue={activeGHGUnit}
                >
                  {Object.keys(ghg_units).map((unit) => (
                    <option key={`unit-opt-${unit}`} value={unit}>
                      {ghg_units[unit]}
                    </option>
                  ))}
                </Form.Control>
              </Form.Group>
            </h5>
            <div style={{ ...row, marginBottom: '6em' }}>
              <EnergyTracerMultiLineChart
                data={!convertedGHGData ? [] : convertedGHGData}
                unit={short_ghg_units[activeGHGUnit]}
                keys={activeKeys}
                baseline={Object.keys(baselineData).length > 0 && showBaselineData}
                baseline2={Object.keys(baseline2Data).length > 0 && showBaseline2Data}
                showSummary={GHGSummary}
              />
              <GraphActionContainer>
                <ChartToggle
                  onClick={() => {
                    setGHGSummary((prev) => !prev);
                  }}
                  state={GHGSummary ? 1 : 0}
                  title={'Toggle Selection Total'}
                  variant={3}
                />
                <ChartDownload
                  organization_id={organization.id}
                  data={convertedGHGData}
                  headers={chartDownloadHeaders}
                  keys={chartDownloadKeys}
                  chart_type={'line'}
                />
              </GraphActionContainer>
            </div>
          </>
        )}
      </div>

    </div>
  );
}
